/**
 * The club and jersey number override on a Reel Option (CAE-1610).
 *
 * Adding an option freezes who its pick is about from the feed: the club of
 * the roster that held the player, and the number that roster carried. The
 * feed can be short of either (an NBA game before its team ids arrive, a
 * roster row with a blank number), so the Operator can set both here. The
 * override sits beside the frozen value, never over it: clearing a field puts
 * the feed's answer back.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { resolvePickName } from '@s2w/core';
import { clubsApi } from '../../../../api/clubs';
import type {
  ContestDetailReelOption,
  ReelOptionParticipantPayload,
} from '../../../../api/contests';
import { clubRosterQueryKey, retrySportQuery } from '../../../sports/sport-queries';

/** How the Operator may write a number: one or two digits, as every NBA number is. */
const JERSEY_NUMBER_PATTERN = /^[0-9]{1,2}$/;

/** The select's value for "no override": the feed's club, whatever it is. */
const FEED_CLUB = '';

export interface ReelOptionParticipantDialogProps {
  open: boolean;
  option: ContestDetailReelOption | null;
  /** The Contest's Sport, whose clubs the override may name. */
  sportId: string | undefined;
  saving?: boolean;
  onClose: () => void;
  onSave: (participant: ReelOptionParticipantPayload) => void;
}

export const ReelOptionParticipantDialog = ({
  open,
  option,
  sportId,
  saving = false,
  onClose,
  onSave,
}: ReelOptionParticipantDialogProps) => {
  const [club, setClub] = useState<string>(FEED_CLUB);
  const [number, setNumber] = useState<string>('');

  // Seeded from the option each time one is opened, so a dialog reused across
  // rows never shows the previous row's draft.
  useEffect(() => {
    if (!open) return;
    setClub(option?.clubAbbreviationOverride ?? FEED_CLUB);
    setNumber(option?.jerseyNumberOverride ?? '');
  }, [open, option?.id, option?.clubAbbreviationOverride, option?.jerseyNumberOverride]);

  const clubsQuery = useQuery({
    queryKey: clubRosterQueryKey(sportId ?? ''),
    queryFn: () => clubsApi.list(sportId ?? ''),
    enabled: open && sportId !== undefined,
    retry: retrySportQuery,
  });
  const clubs = clubsQuery.data?.items ?? [];

  const trimmedNumber = number.trim();
  const numberInvalid = trimmedNumber !== '' && !JERSEY_NUMBER_PATTERN.test(trimmedNumber);

  const feedClub = option?.clubAbbreviation ?? null;
  const feedNumber = option?.jerseyNumber ?? null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit club &amp; number</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            select
            size="small"
            label="Club"
            value={club}
            onChange={(event) => setClub(event.target.value)}
            disabled={saving || clubsQuery.isPending}
            helperText={
              clubsQuery.isError
                ? 'Could not load the Sport’s clubs.'
                : feedClub
                  ? `The feed placed ${option ? resolvePickName(option) : 'this pick'} with ${feedClub}. Leave as is to keep it.`
                  : 'The feed could not place this pick with a club. Choose one so the jersey and badge can show.'
            }
          >
            <MenuItem value={FEED_CLUB}>
              {feedClub ? `Feed’s club (${feedClub})` : 'No club (as the feed had it)'}
            </MenuItem>
            {clubs.map((candidate) => (
              <MenuItem key={candidate.id} value={candidate.abbreviation}>
                {candidate.abbreviation} · {candidate.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Jersey number"
            placeholder={feedNumber ?? 'None'}
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            disabled={saving}
            error={numberInvalid}
            helperText={
              numberInvalid
                ? 'One or two digits.'
                : feedNumber
                  ? `The feed’s number is ${feedNumber}. Leave empty to keep it.`
                  : 'The feed gave no number. Leave empty to show the jersey without one.'
            }
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 2 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={option === null || saving || numberInvalid}
          onClick={() =>
            onSave({
              clubAbbreviationOverride: club === FEED_CLUB ? null : club,
              jerseyNumberOverride: trimmedNumber === '' ? null : trimmedNumber,
            })
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
